import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";
import { Select, Space, Tag, Tooltip } from "antd";
import toast from "react-hot-toast";
import { getFacilities } from "../../services/facilityService";
import { getInstitutions } from "../../services/institutionService";
import DownloadButton from "../../components/Buttons/DownloadButton";
import { generatePDF } from "../../utils/pdf";

const FacilitiesReport = () => {
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFacilities();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = facilities;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        `${item.name} ${item.institution?.name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution.toString()
      );
    }

    setFilteredFacilities(filtered);
  }, [searchTerm, facilities, selectedInstitution]);

  const loadFacilities = async () => {
    setLoading(true);
    try {
      const { data } = await getFacilities();
      setFacilities(data.data);
      setFilteredFacilities(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load facilities");
    } finally {
      setLoading(false);
    }
  };

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const handleDownloadPDF = () => {
    const title = "Specialized Facilities Report";
    const fileName = "facilities-report.pdf";

    const columns = ["Facility", "Institution", "Description"];
    const rows = filteredFacilities.map((facility) => [
      facility.name,
      facility.institution?.name || "N/A",
      facility.description || "N/A",
    ]);

    generatePDF({ title, columns, rows, fileName, orientation: "landscape" });
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Facility",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Institution",
      dataIndex: ["institution", "name"],
      key: "institution",
      render: (text) => (
        <span className="text-gray-700 text-sm">{text || "N/A"}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;
        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <PageHeading title="Facilities Report" subtitle1="Reports" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Facilities Report"
            count={filteredFacilities.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchAdd={false}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredFacilities.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                <Select
                  multiple={false}
                  id="institutions"
                  defaultValue={selectedInstitution}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedInstitution(value);
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Institution
                  </Select.Option>
                  {institutions &&
                    institutions.length > 0 &&
                    institutions.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          <DownloadButton
            onDownload={handleDownloadPDF}
            title="Download Report"
          />
        </div>
      </div>
    </>
  );
};

export default FacilitiesReport;
